'use client';

import { memo as Memo, useMemo, useEffect, useState, useCallback, Fragment } from 'react';
import dayjs from 'dayjs';

//* HOC's
import withUIContext from '@context/consumerHOC/UIConsumer';
import withLanguageContext from '@/context/consumerHOC/LanguageConsumer';

//* Helpers
import breakString from '@helpers/breakString';

//* Components
import Page from '@components/global/Page';
import Text from '@/components/global/Text';
import Image from '@components/global/Image';
import MinTitle from '@components/global/MinTitle';
import TableItem from '@components/global/TableItem';
import CustomLink from '@components/global/CustomLink';
import HTMLContainer from '@components/global/HTMLContainer';
import TableContent from '@components/cfModules/TableContent';
import ContentBuilder from '@components/cfModules/ContentBuilder';
import IndividualTopSection from '@components/global/IndividualTopSection';

//* Styles
import WorkIndividualStyle from '@/styles/pages/WorkIndividualStyle';

const WorkIndividual = ({ fields, portfolioData, lightHeader, toggleLightHeader, translate }) => {
	//! States
	const [hoverKey, setHoverKey] = useState();

	const portfolioTypeWithContent = useMemo(() => {
		let obj = {};

		if (portfolioData) {
			const items = Object.values(portfolioData.items);
			for (let i = 0; i < items.length; i++) {
				obj = {
					...obj,
					[items[i].fields.serviceType]: obj?.[items[i].fields.serviceType] ? [...obj[items[i].fields.serviceType], items[i]] : [items[i]],
				};
			}
		}
		return obj;
	}, [portfolioData]);

	useEffect(() => {
		toggleLightHeader(fields?.lightLogo);
	}, [fields]);

	useEffect(() => {
		window.addEventListener('scroll', onScroll);

		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	}, [fields, lightHeader]);

	const onScroll = useCallback(() => {
		if (fields?.lightLogo) {
			if (window.scrollY > window.innerHeight / 2 && lightHeader) {
				toggleLightHeader();
			} else if (window.scrollY <= window.innerHeight / 2 && !lightHeader) {
				toggleLightHeader(true);
			}
		}
	}, [fields, lightHeader]);

	const relItemsObj = useMemo(() => {
		if (portfolioTypeWithContent && fields) {
			const key = portfolioTypeWithContent[fields.serviceType].findIndex((o) => o.fields.slug === fields.slug);

			const arr = [];
			const isLength = portfolioTypeWithContent[fields.serviceType]?.length > 1;

			const first = isLength && (key === portfolioTypeWithContent[fields.serviceType].length - 1 ? 0 : key + 1);
			const next = isLength && (first === portfolioTypeWithContent[fields.serviceType].length - 1 ? 0 : first + 1);

			typeof first === 'number' && arr.push(portfolioTypeWithContent[fields.serviceType]?.[first]);
			typeof next === 'number' && arr.push(portfolioTypeWithContent[fields.serviceType]?.[next]);
			return arr;
		}
		return [];
	}, [portfolioTypeWithContent, fields]);

	const relItems = useMemo(() => {
		return (
			portfolioTypeWithContent &&
			relItemsObj &&
			relItemsObj.map((item, index) => {
				return (
					item && (
						<div
							key={index}
							onMouseLeave={setHoverKey}
							className={'portfolioItem'}
							onMouseEnter={() => setHoverKey(index)}>
							<CustomLink
								url={`/portfolio/${item.fields.slug}`}
								content={
									<Fragment>
										<div className={'image_video_cont'}>
											{hoverKey === index ? (
												<video
													muted
													autoPlay
													src={item.fields.shortVideoLandscape.fields.file.url}
												/>
											) : null}
											{
												<Image
													className={'image_video'}
													alt={item.fields.smallImageLandscape.fields.title}
													src={item.fields.smallImageLandscape.fields.file.url}
												/>
											}
										</div>

										<Text
											tag={'h5'}
											className={`h6 font-poppins font-medium title`}>
											{item.fields.title}
										</Text>

										<Text className={`p-m font-poppins font-regular description`}>{item.fields.workType}</Text>

										<Text className={`p-m font-poppins font-italic createdDate`}>{dayjs(item.fields.createdAt).format('YYYY')}</Text>
									</Fragment>
								}
							/>
						</div>
					)
				);
			})
		);
	}, [fields, portfolioTypeWithContent, hoverKey]);

	const mainStyle = useMemo(() => {
		if (fields?.backgroundColor) {
			return {
				style: {
					backgroundColor: fields.backgroundColor,
					color: fields.darkText ? 'var(--black)' : 'var(--white)',
				},
			};
		}
	}, [fields]);

	useEffect(() => {
		if (fields?.backgroundColor) {
			if (document.querySelector('.tableWrap')) {
				document.querySelector('.tableWrap').style.setProperty('border-color', fields.darkText ? 'var(--black)' : 'var(--white)');
			}
		}
	}, [fields?.backgroundColor]);

	return fields && portfolioTypeWithContent ? (
		<Page mainProps={mainStyle}>
			<WorkIndividualStyle className={'SinglePortfolioPage'}>
				<IndividualTopSection
					underline={false}
					title={fields.title}
					company={fields.company}
					deliverables={fields.deliverables}
					category={fields.category.fields.name}
					image={{ url: fields.banner.fields.file.url, alt: fields.banner.fields.title }}
				/>

				<HTMLContainer>
					<div className={'sectionItem'}>
						<TableItem
							active
							underline
							className={'one-item'}
							description={fields.description}
							visitWebsiteUrl={fields.visitWebsiteUrl}
							title={translate('portfolioSingleSubtitle')}
						/>
					</div>
				</HTMLContainer>

				<HTMLContainer>
					<div className={`section-item portfolioSingleCont`}>
						<ContentBuilder data={fields.content} />

						<TableContent data={fields} />
					</div>

					{relItems?.length >= 0 ? (
						<div className='section-item'>
							<MinTitle
								active
								title={breakString('more</br> projects')}
							/>

							<div className={'portfolioWrap'}>{relItems}</div>
						</div>
					) : null}
				</HTMLContainer>
			</WorkIndividualStyle>
		</Page>
	) : null;
};

export default withUIContext(withLanguageContext(WorkIndividual, ['translate']), ['lightHeader', 'toggleLightHeader']);
